import { useState } from "react";
import { T } from "../../config/theme.js";
import { api } from "../../hooks/useApi.js";
import { Btn } from "../ui/index.jsx";

export default function GuidePage({ onBack, token, initialTab = "guide" }) {
  const [tab, setTab] = useState(initialTab); // "guide" | "feedback"
  const [feedback, setFeedback] = useState("");
  const [rating, setRating] = useState(0);
  const [feedbackType, setFeedbackType] = useState("general");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const submitFeedback = async () => {
    if (!feedback.trim()) return;
    setSending(true);
    try {
      await api("/feedback", {
        method: "POST",
        body: JSON.stringify({
          type: feedbackType,
          rating,
          message: feedback.trim(),
        }),
      }, token);
      setSubmitted(true);
      setFeedback("");
      setRating(0);
    } catch (e) {
      alert("전송에 실패했습니다. 다시 시도해주세요.");
    }
    setSending(false);
  };

  const guideSteps = [
    {
      icon: "1",
      title: "종목 등록하기",
      desc: "대시보드에서 '종목 추가' 버튼을 눌러 보유 중인 종목이나 관심 종목을 등록하세요. 보유 종목과 대기 종목을 구분해서 관리할 수 있어요.",
    },
    {
      icon: "2",
      title: "리포트 확인하기",
      desc: "등록한 종목의 상태 리포트가 매일 오전/오후 자동으로 생성됩니다. 종목 카드에서 '상세 보기'를 눌러 데이터 변화 요약, 펀더멘털, 뉴스/공시를 확인하세요.",
    },
    {
      icon: "3",
      title: "상태 라벨 이해하기",
      desc: "각 종목에는 데이터 기반 상태 라벨이 표시됩니다.",
      labels: [
        { name: "변화 낮음", color: "#22c55e", desc: "주요 지표 안정" },
        { name: "변화 발생", color: "#f59e0b", desc: "일부 변화 감지" },
        { name: "변동성 확대", color: "#ef4444", desc: "변동폭 확대 구간" },
        { name: "정보 점검 필요", color: "#3b82f6", desc: "데이터 점검 필요" },
      ],
    },
    {
      icon: "4",
      title: "투자 원칙 설정하기",
      desc: "종목별로 손절 기준, 목표 수익률, 보유 기간 등 나만의 투자 원칙을 설정하세요. 원칙에서 벗어나면 Pause가 알려드려요.",
    },
    {
      icon: "5",
      title: "AI 상담 활용하기",
      desc: "종목 카드에서 'AI 상담' 버튼을 눌러 데이터 기반 질문을 해보세요. Pause는 매수/매도 추천 대신, 데이터와 원칙을 기반으로 판단 근거를 정리해드려요.",
    },
    {
      icon: "6",
      title: "시장 분석 확인하기",
      desc: "하단 '시장분석' 탭에서 시장 전체 흐름에 대한 질문을 할 수 있어요. 코스피/코스닥 동향, 섹터 흐름, 외국인 수급 등을 확인하세요.",
    },
  ];

  const feedbackTypes = [
    { value: "general", label: "일반 의견" },
    { value: "bug", label: "버그 신고" },
    { value: "feature", label: "기능 요청" },
    { value: "ux", label: "사용성 개선" },
  ];

  return (
    <div style={{ maxWidth: 640, margin: "0 auto", padding: "0 20px 100px" }}>
      {/* 헤더 */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "20px 0" }}>
        <Btn variant="ghost" onClick={onBack} style={{ fontSize: 13 }}>← 돌아가기</Btn>
      </div>

      {/* 탭 */}
      <div style={{ display: "flex", gap: 0, marginBottom: 32, borderBottom: `1px solid ${T.border}` }}>
        {[
          { key: "guide", label: "사용 가이드" },
          { key: "feedback", label: "피드백 보내기" },
        ].map(t => (
          <div
            key={t.key}
            onClick={() => { setTab(t.key); setSubmitted(false); }}
            style={{
              padding: "14px 24px", cursor: "pointer", fontSize: 15, fontWeight: tab === t.key ? 700 : 500,
              color: tab === t.key ? T.accent : T.textMuted,
              borderBottom: tab === t.key ? `2px solid ${T.accent}` : "2px solid transparent",
              transition: "all 0.2s",
            }}
          >
            {t.label}
          </div>
        ))}
      </div>

      {/* ─── 사용 가이드 탭 ─── */}
      {tab === "guide" && (
        <div>
          <div style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 24, fontWeight: 800, color: T.text, marginBottom: 8 }}>Pause 사용 가이드</h2>
            <p style={{ fontSize: 14, color: T.textDim, lineHeight: 1.7 }}>
              Pause는 매수/매도를 추천하지 않습니다. 내 종목의 데이터 변화를 정리하고, 내가 세운 원칙과 비교해서 스스로 판단할 수 있도록 돕는 서비스예요.
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {guideSteps.map((step, i) => (
              <div key={i} style={{
                background: T.card, borderRadius: 14, border: `1px solid ${T.border}`,
                padding: "24px 20px",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 12 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%", background: T.accent,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 16, fontWeight: 800, color: "#fff", flexShrink: 0,
                  }}>
                    {step.icon}
                  </div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: T.text }}>{step.title}</div>
                </div>
                <div style={{ fontSize: 14, color: T.textDim, lineHeight: 1.7, paddingLeft: 50 }}>
                  {step.desc}
                </div>
                {step.labels && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12, paddingLeft: 50 }}>
                    {step.labels.map((l, j) => (
                      <div key={j} style={{
                        display: "flex", alignItems: "center", gap: 6,
                        padding: "6px 12px", borderRadius: 8, background: T.bg,
                        fontSize: 12,
                      }}>
                        <span style={{ width: 8, height: 8, borderRadius: "50%", background: l.color }} />
                        <span style={{ fontWeight: 600, color: l.color }}>{l.name}</span>
                        <span style={{ color: T.textMuted }}>— {l.desc}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* 추가 안내 */}
          <div style={{
            marginTop: 32, padding: "20px 24px", background: T.accent + "10",
            borderRadius: 14, border: `1px solid ${T.accent}25`,
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: T.accent, marginBottom: 8 }}>
              Pause가 하지 않는 것
            </div>
            <div style={{ fontSize: 13, color: T.textDim, lineHeight: 1.8 }}>
              • 특정 종목의 매수/매도를 추천하지 않습니다<br />
              • 수익률을 보장하지 않습니다<br />
              • 미래 주가를 예측하지 않습니다<br />
              • 모든 투자 결정은 사용자 본인의 판단과 책임입니다
            </div>
          </div>
        </div>
      )}

      {/* ─── 피드백 탭 ─── */}
      {tab === "feedback" && (
        <div>
          {submitted ? (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <div style={{ fontSize: 48, marginBottom: 20 }}>✅</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: T.text, marginBottom: 8 }}>피드백을 보냈어요!</div>
              <div style={{ fontSize: 14, color: T.textDim, marginBottom: 24 }}>소중한 의견 감사합니다. 더 나은 Pause를 만드는 데 반영하겠습니다.</div>
              <Btn variant="primary" onClick={() => setSubmitted(false)}>추가 피드백 보내기</Btn>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 28 }}>
                <h2 style={{ fontSize: 24, fontWeight: 800, color: T.text, marginBottom: 8 }}>의견을 들려주세요</h2>
                <p style={{ fontSize: 14, color: T.textDim, lineHeight: 1.7 }}>
                  Pause를 사용하면서 느낀 점, 개선했으면 하는 부분, 필요한 기능이 있다면 알려주세요.
                </p>
              </div>

              {/* 피드백 유형 */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 10 }}>피드백 유형</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {feedbackTypes.map(ft => (
                    <div
                      key={ft.value}
                      onClick={() => setFeedbackType(ft.value)}
                      style={{
                        padding: "8px 16px", borderRadius: 10, fontSize: 13, cursor: "pointer",
                        fontWeight: feedbackType === ft.value ? 700 : 500,
                        background: feedbackType === ft.value ? T.accent + "15" : T.bg,
                        color: feedbackType === ft.value ? T.accent : T.textMuted,
                        border: `1px solid ${feedbackType === ft.value ? T.accent + "40" : T.border}`,
                        transition: "all 0.15s",
                      }}
                    >
                      {ft.label}
                    </div>
                  ))}
                </div>
              </div>

              {/* 만족도 */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 10 }}>전반적인 만족도</div>
                <div style={{ display: "flex", gap: 8 }}>
                  {[1, 2, 3, 4, 5].map(n => (
                    <div
                      key={n}
                      onClick={() => setRating(n)}
                      style={{
                        width: 44, height: 44, borderRadius: 12, cursor: "pointer",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 20,
                        background: rating >= n ? T.accent + "15" : T.bg,
                        border: `1px solid ${rating >= n ? T.accent + "40" : T.border}`,
                        transition: "all 0.15s",
                      }}
                    >
                      {rating >= n ? "★" : "☆"}
                    </div>
                  ))}
                </div>
              </div>

              {/* 내용 */}
              <div style={{ marginBottom: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 10 }}>내용</div>
                <textarea
                  value={feedback}
                  onChange={e => setFeedback(e.target.value)}
                  placeholder="어떤 점이 좋았는지, 어떤 점이 불편했는지, 어떤 기능이 있으면 좋겠는지 자유롭게 적어주세요."
                  style={{
                    width: "100%", minHeight: 140, padding: "14px 16px",
                    background: T.card, border: `1px solid ${T.border}`,
                    borderRadius: 12, color: T.text, fontSize: 14,
                    lineHeight: 1.7, resize: "vertical", outline: "none",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                  }}
                  onFocus={e => e.target.style.borderColor = T.accent + "60"}
                  onBlur={e => e.target.style.borderColor = T.border}
                />
              </div>

              {/* 전송 버튼 */}
              <Btn
                variant="primary"
                onClick={submitFeedback}
                disabled={!feedback.trim() || sending}
                style={{
                  width: "100%", justifyContent: "center", padding: "14px",
                  fontSize: 15, opacity: !feedback.trim() || sending ? 0.5 : 1,
                }}
              >
                {sending ? "보내는 중..." : "피드백 보내기"}
              </Btn>
            </>
          )}
        </div>
      )}
    </div>
  );
}
